import { useDeviceStorage } from '../adapters/ports.js';

// Local persistence for the sales and auction houses the dealer follows, through the
// host's device-storage port. A follow is a fact about this device until the server
// holds them: another device signed in as the same dealer starts with none. Saving
// them to the account is a separate piece of work.
//
// V2 STORES IDS. v1 stored a sale's NAME and a house's slug, and both were the
// bug: a sale renamed overnight orphaned the follow, a weekly name repeating
// pointed one follow at two different sales, and a name arriving with a stray
// space read as a sale nobody follows. v2 stores the sale's minted `saleRefId` and
// the house's Mongo `_id`, neither of which anything can edit.
//
//   { sales: [saleRefId], houses: [houseId], pending: { sales: [name], houses: [slug] } }
//
// `pending` is how a v1 value survives. A name can only become an id once the
// sale index has loaded, and a name that names two sales cannot become one at
// all — so an unresolved entry STAYS in `pending` and is tried again on every
// later load. Nothing is dropped for being unresolvable yet. The v1 key is
// removed only once `pending` is empty, so a dealer who never gets a resolvable
// index still has their follows on the device.
const FOLLOWS_KEY = 'aac.follows.v2';
const LEGACY_FOLLOWS_KEY = 'aac.follows.v1';

const strings = (value) => (Array.isArray(value) ? value.filter((each) => typeof each === 'string') : []);

const pendingOf = (value) => ({
  sales: strings(value?.sales),
  houses: strings(value?.houses)
});

const nothingPending = (pending) => pending.sales.length === 0 && pending.houses.length === 0;

// Never rejects, and never returns a half-shape: the port answers null for both a
// missing value and a malformed one, and the field-by-field read below turns anything
// else unexpected into no follows.
//
// A device still on v1 answers with no follows and every stored name pending,
// which is the honest reading — the names are held, and none of them is an id
// yet.
export async function getFollows() {
  const storage = useDeviceStorage();
  const parsed = await storage.getJson(FOLLOWS_KEY);
  if (!parsed || typeof parsed !== 'object') {
    const legacy = await storage.getJson(LEGACY_FOLLOWS_KEY);
    if (!legacy || typeof legacy !== 'object') {
      return { sales: [], houses: [], pending: { sales: [], houses: [] } };
    }
    return { sales: [], houses: [], pending: pendingOf(legacy) };
  }
  return {
    sales: strings(parsed.sales),
    houses: strings(parsed.houses),
    pending: pendingOf(parsed.pending)
  };
}

export async function setFollows({ sales = [], houses = [], pending } = {}) {
  const storage = useDeviceStorage();
  const stillPending = pendingOf(pending);
  await storage.setJson(FOLLOWS_KEY, {
    sales: strings(sales),
    houses: strings(houses),
    pending: stillPending
  });
  // The v1 key goes only when there is nothing left to resolve out of it.
  if (nothingPending(stillPending)) {
    await storage.remove(LEGACY_FOLLOWS_KEY);
  }
}

export async function clearFollows() {
  const storage = useDeviceStorage();
  await storage.remove(FOLLOWS_KEY);
  await storage.remove(LEGACY_FOLLOWS_KEY);
}

export const FOLLOWS_STORAGE_KEY = FOLLOWS_KEY;
export const LEGACY_FOLLOWS_STORAGE_KEY = LEGACY_FOLLOWS_KEY;
export const followStorage = { getFollows, setFollows, clearFollows };
export default followStorage;
